/**
 * Skeleton Loading Component
 * 
 * A reusable skeleton component with shimmer animation
 * Supports both light and dark mode with theme-aware styling
 * 
 * Usage:
 * <Skeleton /> // Default rectangle
 * <Skeleton width="100px" height="20px" />
 * <Skeleton circle width="50px" height="50px" />
 * <Skeleton className="mb-4" />
 */

import { type CSSProperties } from 'react';

interface SkeletonProps {
  width?: string | number;
  height?: string | number;
  circle?: boolean;
  className?: string;
  style?: CSSProperties;
  count?: number;
}

export default function Skeleton({ 
  width,
  height,
  circle = false,
  className = '',
  style = {},
  count = 1
}: SkeletonProps) {
  const skeletonStyle: CSSProperties = {
    width: width || '100%',
    height: height || '1rem',
    borderRadius: circle ? '50%' : '0.5rem',
    ...style,
  };

  const skeletonElements = Array.from({ length: count }, (_, index) => (
    <div
      key={index}
      className={`skeleton-loader ${className}`}
      style={skeletonStyle}
      aria-busy="true"
      aria-live="polite"
    />
  ));

  return count === 1 ? skeletonElements[0] : <>{skeletonElements}</>;
}

/**
 * Predefined Skeleton Layouts
 */

// Profile Card Skeleton
export function ProfileSkeleton() {
  return (
    <div className="
      dark:bg-dark-surface
      bg-light-surface
      rounded-lg
      shadow-lg
      border-2
      dark:border-theme
      border-theme
      p-8
    ">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <Skeleton width="150px" height="36px" />
        <Skeleton circle width="40px" height="40px" />
      </div>

      {/* Avatar Section */}
      <div className="flex items-center gap-6 mb-8 pb-8 border-b dark:border-dark-hover border-gray-200">
        <Skeleton circle width="96px" height="96px" />
        <div className="flex-1 space-y-3">
          <Skeleton width="200px" height="28px" />
          <Skeleton width="150px" height="20px" />
        </div>
      </div>

      {/* User Details */}
      <div className="space-y-6 mb-8">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-start gap-4">
            <Skeleton circle width="40px" height="40px" />
            <div className="flex-1 space-y-2">
              <Skeleton width="100px" height="16px" />
              <Skeleton width="70%" height="20px" />
            </div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="space-y-4">
        <Skeleton width="100%" height="48px" />
        <Skeleton width="100%" height="48px" />
        <Skeleton width="100%" height="48px" />
      </div>
    </div>
  );
}

// Task Card Skeleton
export function TaskCardSkeleton() {
  return (
    <div className="
      relative
      p-4
      rounded-lg
      border-2
      dark:border-theme
      border-theme
      dark:bg-dark-surface
      bg-light-surface
      shadow-md
    ">
      {/* Drag handle */}
      <div className="absolute top-2 right-2">
        <Skeleton circle width="20px" height="20px" />
      </div>

      {/* Header: Title and Status */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <Skeleton width="70%" height="24px" className="flex-1" />
        <Skeleton width="80px" height="24px" />
      </div>

      {/* Description */}
      <div className="mb-3 space-y-2">
        <Skeleton width="100%" height="16px" />
        <Skeleton width="60%" height="16px" />
      </div>

      {/* Tags */}
      <div className="flex items-center gap-2 mb-3">
        <Skeleton circle width="16px" height="16px" />
        <Skeleton width="60px" height="24px" />
        <Skeleton width="70px" height="24px" />
      </div>

      {/* Metadata row */}
      <div className="flex items-center gap-3 mb-3">
        <Skeleton width="60px" height="24px" />
        <Skeleton width="80px" height="16px" />
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-2 pt-3 border-t-2 dark:border-theme border-theme">
        <Skeleton circle width="32px" height="32px" />
        <Skeleton circle width="32px" height="32px" className="ml-auto" />
      </div>
    </div>
  );
}

// Dashboard Skeleton (with multiple task columns)
export function DashboardSkeleton() {
  return (
    <div className="min-h-screen py-8 px-4 dark:bg-dark-bg bg-light-bg">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <Skeleton width="250px" height="36px" />
            <div className="flex items-center gap-4">
              <Skeleton width="120px" height="40px" />
              <Skeleton circle width="40px" height="40px" />
            </div>
          </div>
          <Skeleton width="300px" height="20px" />
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {[1, 2, 3].map((i) => (
            <div key={i} className="dark:bg-dark-surface bg-light-surface rounded-lg p-4 border-2 dark:border-theme border-theme">
              <Skeleton width="100px" height="16px" className="mb-2" />
              <Skeleton width="50px" height="32px" />
            </div>
          ))}
        </div>

        {/* Kanban Columns */}
        <div className="flex gap-4 overflow-x-auto pb-4">
          {[1, 2, 3].map((col) => (
            <div key={col} className="flex-1 min-w-[300px]">
              <div className="
                p-4
                rounded-lg
                border-2
                dark:border-theme
                border-theme
                dark:bg-dark-surface
                bg-light-surface
              ">
                <Skeleton width="120px" height="24px" className="mb-2" />
                <Skeleton width="80px" height="16px" className="mb-4" />
                <div className="space-y-3">
                  {[1, 2].map((card) => (
                    <TaskCardSkeleton key={card} />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Calendar Page Skeleton
export function CalendarSkeleton() {
  return (
    <div className="min-h-screen py-8 px-4 dark:bg-dark-bg bg-light-bg">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Skeleton circle width="32px" height="32px" />
              <Skeleton width="200px" height="36px" />
            </div>
            <Skeleton width="150px" height="40px" />
          </div>
          <Skeleton width="250px" height="20px" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Calendar */}
          <div className="
            p-6
            rounded-lg
            dark:bg-dark-surface
            bg-light-surface
            shadow-lg
            border-2
            dark:border-theme
            border-theme
          ">
            <Skeleton width="100%" height="400px" />
          </div>

          {/* Tasks for selected date */}
          <div className="
            p-6
            rounded-lg
            dark:bg-dark-surface
            bg-light-surface
            shadow-lg
          ">
            <Skeleton width="200px" height="28px" className="mb-4" />
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <TaskCardSkeleton key={i} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Archive Page Skeleton
export function ArchiveSkeleton() {
  return (
    <div className="min-h-screen py-8 px-4 dark:bg-dark-bg bg-light-bg">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Skeleton circle width="32px" height="32px" />
              <Skeleton width="200px" height="36px" />
            </div>
            <Skeleton width="180px" height="40px" />
          </div>
          <Skeleton width="250px" height="20px" />
        </div>

        {/* Task Count */}
        <Skeleton width="150px" height="16px" className="mb-4" />

        {/* Task Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <TaskCardSkeleton key={i} />
          ))}
        </div>
      </div>
    </div>
  );
}

// Auth Loading Skeleton (for App.tsx)
export function AuthLoadingSkeleton() {
  return (
    <div className="min-h-screen flex items-center justify-center dark:bg-dark-bg bg-light-bg">
      <div className="flex flex-col items-center gap-4">
        <Skeleton circle width="64px" height="64px" />
        <Skeleton width="200px" height="24px" />
        <Skeleton width="150px" height="16px" />
      </div>
    </div>
  );
}
